import { useEffect } from 'react';
import { NavLink, Outlet, Link, useLocation } from 'react-router-dom';
import { COMPANY, COMPANY_URL, TAGLINE, WEEKLY_ARCHIVE_URL } from '../lib/site';

// Shared chrome for every SPA route. The weekly-brief rung is a plain <a>: it is
// server-rendered on chloe, so it must be a document navigation, not a route.
const NAV = [
  { to: '/meetings', label: 'Meetings' },
  { to: '/blip', label: 'Blip a doc' },
] as const;

function navClass({ isActive }: { isActive: boolean }): string {
  return [
    'relative py-1 font-mono text-xs uppercase tracking-[0.14em] transition-colors',
    isActive ? 'text-teal' : 'text-muted hover:text-ink',
    isActive
      ? 'after:absolute after:inset-x-0 after:-bottom-px after:h-px after:bg-teal after:content-[""]'
      : '',
  ].join(' ');
}

export default function Layout() {
  const { pathname } = useLocation();

  // BrowserRouter keeps the scroll offset across a route change, which lands you
  // halfway down a page you have not seen yet.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div className="relative flex min-h-screen flex-col">
      <div className="bg-grid" />

      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-panel focus:px-4 focus:py-2 focus:font-mono focus:text-xs focus:text-teal focus:shadow-lift"
      >
        Skip to content
      </a>

      <header className="relative z-10 border-b border-edge">
        <div className="mx-auto flex max-w-5xl flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <Link
            to="/"
            className="wordmark font-mono text-xl font-bold tracking-tight text-ink transition-colors hover:text-teal"
          >
            BriefBlip
            <span className="ml-1 inline-block h-1.5 w-1.5 translate-y-[-2px] rounded-full bg-clay align-middle" />
          </Link>

          <nav aria-label="Primary" className="flex items-center gap-5 sm:gap-6">
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to} className={navClass}>
                {item.label}
              </NavLink>
            ))}
            <a
              href={WEEKLY_ARCHIVE_URL}
              className="py-1 font-mono text-xs uppercase tracking-[0.14em] text-muted transition-colors hover:text-ink"
            >
              Weekly briefs
            </a>
          </nav>
        </div>
      </header>

      <main id="main" className="relative z-10 flex-1">
        <Outlet />
      </main>

      <footer className="relative z-10 mt-16 border-t border-edge">
        <div className="mx-auto flex max-w-5xl flex-col gap-1 px-5 py-8 text-center sm:flex-row sm:items-baseline sm:justify-between sm:text-left">
          <p className="text-sm font-semibold text-ink">
            &copy; {new Date().getFullYear()}{' '}
            <a href={COMPANY_URL} className="text-teal hover:underline">
              {COMPANY}
            </a>
          </p>
          <p className="font-mono text-xs tracking-wide text-muted">{TAGLINE}</p>
        </div>
      </footer>
    </div>
  );
}
